// Refonte du suivi, lot 6 : « Depuis le dernier rendez-vous » et « À regarder
// en premier ».
//
// Garde-fou clinique : ce module ne déduit RIEN. Il rend des faits (valeurs
// reçues, décomptes, dates, mots de la cliente, notes d'Anissa) et des
// répétitions repérées par des règles fixes et affichées avec leur règle.
// Aucune décision, aucune recommandation, aucun seuil clinique : les seules
// constantes sont des nombres de répétitions, dits à l'écran.

import { clientAppFetch as defaultClientAppFetch } from '../../services/clientAppFetch';
import { clientIdentityFields, hasClientIdentity } from '../../services/clientIdentity';
import { AXIS_LABELS, AXIS_VALUE_LABELS, computeFeedbackTrends } from '../../services/feedbackTrends';
import { mealSlotLabel } from '../../services/fetchClientMealSummary';
import { formaterDate, rendezVousRelus } from '../suiviEcran';

// Fenêtre maximale demandée à l'app.
export const JOURS_MAX = 90;

/**
 * Règles de « À regarder en premier » — à valider par Anissa (essai, plan §7).
 * Des répétitions de SES réponses dans l'app, dites telles quelles avec leur
 * règle ; aucun seuil biologique, aucune décision. Choisir quelles réponses
 * signaler est un choix clinique : il appartient à Anissa.
 */
export const REGLES_A_REGARDER = Object.freeze({
  // Réponses dont la répétition est signalée (codes de l'app).
  valeursSignalees: Object.freeze({
    digestion: 'worse', // « Pire »
    fatigue: 'worse', // « Pire »
    energie: 'low', // « Basse »
    faim: 'high', // « Élevée »
  }),
  joursDeSuite: 3, // même réponse N jours de suite ou plus
  memeRepasRemplace: 2, // même repas remplacé N fois ou plus
  memeCreneauSaute: 3, // même repas sauté N fois ou plus
  joursSansRessenti: 5, // N jours terminés de suite sans ressenti
});
/** Ancien nom, gardé pour les lecteurs existants. */
export const REGLES = REGLES_A_REGARDER;

const JOUR_MS = 86400000;
const ISO_JOUR = /^\d{4}-\d{2}-\d{2}$/;
const FORMAT_ZURICH = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit' });

/** Jour local de la cliente (Europe/Zurich), au format AAAA-MM-JJ. */
export function jourZurich(valeur) {
  if (valeur == null || valeur === '') return '';
  if (typeof valeur === 'string' && ISO_JOUR.test(valeur)) return valeur;
  const date = valeur instanceof Date ? valeur : new Date(valeur);
  if (Number.isNaN(date.getTime())) return String(valeur).slice(0, 10);
  return FORMAT_ZURICH.format(date);
}
const jour = jourZurich;
const ecart = (a, b) => Math.round((new Date(`${b}T12:00:00Z`) - new Date(`${a}T12:00:00Z`)) / JOUR_MS);

/** Le dernier rendez-vous relu par Anissa (date, résumé, note), ou null. */
export function dernierRendezVous(client) {
  const relus = rendezVousRelus(client).filter((rdv) => rdv.date);
  return relus[0] || null;
}

/** Nombre de jours à lire depuis une date (au moins 1, au plus JOURS_MAX). */
export function joursDepuis(depuis, maintenant = new Date()) {
  if (!depuis) return 30;
  const n = ecart(jour(depuis), jour(maintenant)) + 1;
  return Math.min(JOURS_MAX, Math.max(1, n));
}

function garderDepuis(liste, depuis, cle = 'date') {
  const debut = jour(depuis);
  return (Array.isArray(liste) ? liste : []).filter((item) => !debut || jour(item?.[cle]) >= debut);
}

/**
 * Lit ressentis et repas depuis une date dans l'app cliente. Ne lève jamais :
 * { ok:false, message } si l'app ne répond pas. `since` est transmis à l'app ;
 * le filtre est refait ici pour qu'une app plus ancienne (qui l'ignore) ne
 * montre jamais de données antérieures au rendez-vous.
 * Rend aussi la période RÉELLEMENT chargée (`debutCharge`, d'après les jours
 * rendus par l'app) et `coupee` quand elle commence après le rendez-vous.
 */
export async function chargerRessenti(client, depuis, {
  fetchClientApp = defaultClientAppFetch,
  maintenant = new Date(),
} = {}) {
  if (!hasClientIdentity(client)) {
    return { ok: false, message: 'Cette cliente n’a pas encore d’accès à l’app.' };
  }
  const debut = depuis ? jour(depuis) : null;
  const query = { ...clientIdentityFields(client), days: joursDepuis(debut, maintenant) };
  if (debut) query.since = debut;
  try {
    const [ressentis, repas] = await Promise.all([
      fetchClientApp('/api/admin/client-feedbacks', { method: 'GET', query }),
      fetchClientApp('/api/admin/client-meal-summary', { method: 'GET', query }),
    ]);
    if (!ressentis?.ok || !repas?.ok) throw new Error('reponse');
    const trouvee = ressentis.found !== false && repas.found !== false;
    const jours = garderDepuis(repas.meals?.days, debut, 'day');
    const joursRendus = jours.map((j) => jour(j.day)).filter(Boolean).sort();
    // L'app qui honore since le renvoie : la période chargée est celle-là (des
    // jours sans repas au début ne coupent pas la période). Une app plus
    // ancienne ne le renvoie pas : on se rabat sur le premier jour rendu.
    const renvoyes = [ressentis.since, repas.since];
    const sinceRenvoye = debut && renvoyes.every((s) => typeof s === 'string' && ISO_JOUR.test(s))
      ? renvoyes.sort()[1]
      : null;
    const debutCharge = sinceRenvoye || joursRendus[0] || debut;
    return {
      ok: true,
      trouvee,
      feedbacks: garderDepuis(ressentis.feedbacks, debut),
      jours,
      debut,
      debutCharge,
      coupee: Boolean(debut && debutCharge && debutCharge > debut),
    };
  } catch {
    return { ok: false, message: 'Les données de l’app n’ont pas pu être chargées. Tu peux préparer la version quand même.' };
  }
}

/** Les quatre tendances, dans l'ordre de l'écran. */
export function quatreTendances(feedbacks) {
  const { axisTrends } = computeFeedbackTrends(feedbacks);
  const ordre = ['energie', 'fatigue', 'digestion', 'faim'];
  return ordre.map((axe) => axisTrends.find((t) => t.axis === axe));
}

/** Ses mots : les notes écrites dans l'app, la plus récente d'abord. */
export function motsDeLaCliente(feedbacks) {
  return [...(feedbacks || [])]
    .filter((f) => typeof f?.note === 'string' && f.note.trim())
    .sort((a, b) => jour(b.date).localeCompare(jour(a.date)))
    .map((f) => ({ date: jour(f.date), texte: f.note.trim() }));
}

/** Repas : décomptes et détail des remplacés / sautés. */
export function repasDepuis(jours) {
  const totaux = { prevus: 0, renseignes: 0, commePrevu: 0, remplaces: 0, sautes: 0 };
  const remplaces = [];
  const sautes = [];
  for (const j of jours || []) {
    if (Number.isInteger(j?.planned)) totaux.prevus += j.planned;
    totaux.renseignes += j?.filled || 0;
    totaux.commePrevu += j?.done || 0;
    totaux.remplaces += j?.replaced || 0;
    totaux.sautes += j?.skip || 0;
    for (const e of j?.entries || []) {
      const repas = e.slot_label || mealSlotLabel(e.slot);
      if (e.status === 'replaced') {
        remplaces.push({ jour: j.day, repas, prevu: e.meal_title || null, remplacePar: e.replaced_choice || null, slot: e.slot });
      } else if (e.status === 'skip') {
        sautes.push({ jour: j.day, repas, prevu: e.meal_title || null, slot: e.slot });
      }
    }
  }
  const tri = (a, b) => b.jour.localeCompare(a.jour);
  return { totaux, remplaces: remplaces.sort(tri), sautes: sautes.sort(tri) };
}

/** Poids : seulement si le suivi du poids est activé pour la cliente. */
export function poidsDepuis(client, feedbacks) {
  const actif = Boolean(client?.weight_tracking_enabled ?? client?.weightTrackingEnabled);
  if (!actif) return null;
  const mesures = (feedbacks || [])
    .filter((f) => typeof f?.weight_kg === 'number' && Number.isFinite(f.weight_kg))
    .map((f) => ({ date: jour(f.date), kg: f.weight_kg }))
    .sort((a, b) => a.date.localeCompare(b.date));
  return { mesures, premiere: mesures[0] || null, derniere: mesures[mesures.length - 1] || null };
}

function plusLonguesSuites(feedbacks, axe, valeur) {
  const jours = [...new Set((feedbacks || [])
    .filter((f) => f?.[axe] === valeur)
    .map((f) => jour(f.date)))].sort();
  const suites = [];
  let debut = null;
  let precedent = null;
  for (const j of jours) {
    if (precedent && ecart(precedent, j) === 1) {
      precedent = j;
    } else {
      if (debut) suites.push({ debut, fin: precedent, n: ecart(debut, precedent) + 1 });
      debut = j;
      precedent = j;
    }
  }
  if (debut) suites.push({ debut, fin: precedent, n: ecart(debut, precedent) + 1 });
  return suites;
}

const dateCourte = (j) => formaterDate(`${j}T12:00:00Z`);

/**
 * « À regarder en premier » : répétitions repérées par des règles fixes.
 * Chaque élément dit le fait, les dates et la règle appliquée. Rien d'autre.
 */
export function aRegarderEnPremier({ feedbacks = [], jours = [], depuis = null, maintenant = new Date(), trouvee = true } = {}) {
  const items = [];
  for (const [axe, valeur] of Object.entries(REGLES_A_REGARDER.valeursSignalees)) {
    for (const suite of plusLonguesSuites(feedbacks, axe, valeur)) {
      if (suite.n < REGLES_A_REGARDER.joursDeSuite) continue;
      const libelle = AXIS_VALUE_LABELS[axe][valeur];
      items.push({
        cle: `${axe}-${suite.debut}`,
        texte: `${AXIS_LABELS[axe]} « ${libelle} » ${suite.n} jours de suite, du ${dateCourte(suite.debut)} au ${dateCourte(suite.fin)}.`,
        regle: `Même réponse ${REGLES_A_REGARDER.joursDeSuite} jours de suite ou plus.`,
      });
    }
  }

  const { remplaces, sautes } = repasDepuis(jours);
  const parPlat = new Map();
  for (const r of remplaces) {
    const cle = `${r.repas}|${r.prevu || ''}`;
    parPlat.set(cle, [...(parPlat.get(cle) || []), r]);
  }
  for (const [cle, liste] of parPlat) {
    if (liste.length < REGLES_A_REGARDER.memeRepasRemplace) continue;
    const [repas, prevu] = cle.split('|');
    const par = [...new Set(liste.map((r) => r.remplacePar).filter(Boolean))];
    items.push({
      cle: `remplace-${cle}`,
      texte: `${repas}${prevu ? ` « ${prevu} »` : ''} remplacé ${liste.length} fois${par.length ? ` (par ${par.map((p) => `« ${p} »`).join(', ')})` : ''}.`,
      regle: `Même repas remplacé ${REGLES_A_REGARDER.memeRepasRemplace} fois ou plus.`,
    });
  }
  const parCreneau = new Map();
  for (const s of sautes) parCreneau.set(s.repas, (parCreneau.get(s.repas) || 0) + 1);
  for (const [repas, n] of parCreneau) {
    if (n < REGLES_A_REGARDER.memeCreneauSaute) continue;
    items.push({
      cle: `saute-${repas}`,
      texte: `${repas} sauté ${n} fois.`,
      regle: `Même repas sauté ${REGLES_A_REGARDER.memeCreneauSaute} fois ou plus.`,
    });
  }

  // Jours terminés sans aucun ressenti noté.
  const notes = new Set((feedbacks || []).map((f) => jour(f.date)));
  const hier = jour(new Date(maintenant.getTime() - JOUR_MS));
  const debut = depuis ? jour(depuis) : null;
  // Cliente inconnue de l'app : pas de « jours sans ressenti ».
  if (trouvee && debut && debut <= hier) {
    let courant = null;
    let n = 0;
    const plages = [];
    for (let i = 0; i <= ecart(debut, hier); i += 1) {
      const j = jour(new Date(new Date(`${debut}T12:00:00Z`).getTime() + i * JOUR_MS).toISOString());
      if (notes.has(j)) {
        if (n) plages.push({ debut: courant, n });
        courant = null;
        n = 0;
      } else {
        courant = courant || j;
        n += 1;
      }
    }
    if (n) plages.push({ debut: courant, n });
    for (const p of plages.filter((x) => x.n >= REGLES_A_REGARDER.joursSansRessenti)) {
      items.push({
        cle: `vide-${p.debut}`,
        texte: `Aucun ressenti noté pendant ${p.n} jours à partir du ${dateCourte(p.debut)}.`,
        regle: `${REGLES_A_REGARDER.joursSansRessenti} jours terminés de suite ou plus sans ressenti.`,
      });
    }
  }
  return items;
}
